// The protection score as a rev counter: a red → amber → green scale with a needle.
// Drawn in small arc segments so every point on the scale has its exact colour.

const STOPS = [
  [0, [217, 45, 32]],     // red
  [40, [247, 144, 9]],    // amber
  [70, [102, 198, 28]],   // light green
  [100, [6, 118, 71]],    // green
];

export function scoreColour(v, { faded = false } = {}) {
  const x = Math.max(0, Math.min(100, v));
  const out = (c) => `rgb(${(faded ? c.map((n) => Math.round(n * 0.22 + 255 * 0.78)) : c).join(',')})`;
  for (let i = 1; i < STOPS.length; i += 1) {
    const [b, cb] = STOPS[i];
    const [a, ca] = STOPS[i - 1];
    if (x <= b) {
      const t = (x - a) / (b - a);
      return out(ca.map((n, k) => Math.round(n + (cb[k] - n) * t)));
    }
  }
  return out(STOPS.at(-1)[1]);
}

/** A readable text colour for the score (the bright scale colours are too light for text). */
export function scoreInk(v) {
  if (v < 40) return '#B42318';
  if (v < 70) return '#B54708';
  return '#067647';
}

const CX = 120;
const CY = 124;
const R = 88;
// 0 = far left, 100 = far right, over the top
const angleFor = (v) => Math.PI * (1 - v / 100);
const point = (v, r) => [CX + r * Math.cos(angleFor(v)), CY - r * Math.sin(angleFor(v))];

function arc(from, to, r) {
  const [x1, y1] = point(from, r);
  const [x2, y2] = point(to, r);
  return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${r} ${r} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

const SEGMENTS = Array.from({ length: 50 }, (_, i) => i * 2);

export default function ScoreGauge({ score, label }) {
  const value = Math.max(0, Math.min(100, Number(score) || 0));
  const needleAngle = -90 + (value / 100) * 180;   // rotate from straight up
  return (
    <figure className="gauge" aria-label={`Protection score ${value} out of 100${label ? `: ${label}` : ''}`} role="img">
      <svg viewBox="0 0 240 144" className="gauge-svg" aria-hidden="true">
        {SEGMENTS.map((s) => (
          // Solid colours (faded ones pre-mixed with white): overlapping translucent segments show seams
          <path key={s} d={arc(s, Math.min(100, s + 2.3), R)} stroke={scoreColour(s + 1, { faded: s + 1 > value })} strokeWidth="18" fill="none" />
        ))}
        {[0, 20, 40, 60, 80, 100].map((t) => {
          // Ticks across the band's outer edge, numbers outside it, so the needle never covers them
          const [x1, y1] = point(t, R + 11);
          const [x2, y2] = point(t, R + 15);
          // 0 and 100 sit under the ends of the band; the rest outside it
          const [tx, ty] = t === 0 || t === 100 ? [point(t, R)[0], CY + 15] : point(t, R + 22);
          return (
            <g key={t}>
              {t > 0 && t < 100 && <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--line-2)" strokeWidth="1.5" />}
              <text x={tx} y={ty + 3} textAnchor="middle" className="gauge-tick">{t}</text>
            </g>
          );
        })}
        <g className="gauge-needle" style={{ transform: `rotate(${needleAngle}deg)` }}>
          <path d={`M${CX - 3.5} ${CY} L${CX} ${CY - R + 4} L${CX + 3.5} ${CY} Z`} fill="var(--navy)" />
        </g>
        <circle cx={CX} cy={CY} r="8" fill="var(--navy)" />
        <circle cx={CX} cy={CY} r="3" fill="var(--surface)" />
      </svg>
      <figcaption className="gauge-reading">
        <span className="gauge-score" style={{ color: scoreInk(value) }}>{value}</span>
        <span className="gauge-of">/ 100</span>
        {label && <span className="gauge-label" style={{ color: scoreInk(value) }}>{label}</span>}
      </figcaption>
    </figure>
  );
}
